import { CRYPT_ASCUAS, DEEP_CRYPT, DUNGEON, NIGHTMARES, WARDEN } from '@somnhold/content';
import { touchGrid } from '../grid.ts';
import { hexRing, hexToWorld } from '../hex.ts';
import { dist2, idiv, imax, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState, Bot, DungeonMark, DungeonRoom, DungeonState, Enemy, Hero } from '../types.ts';
import { huntDrawnBy } from './bots.ts';
import type { EnemyKind } from '@somnhold/content';
import { damageHero, ev, findHero, markedBlow, spawnEnemy } from './combat.ts';
import { placeEruption } from './ascuas.ts';
import { expeditionTick } from './expedition.ts';
import { holdAtStart, mazeTick } from './maze.ts';
import { drinkTick } from './drinker.ts';
import { descentTick, guardianOf, openDescent, pullSpot, puzzleHolds, puzzleTick, wakePuzzle } from './deep.ts';
import { simaDmgBp, simaHallCleared, simaTick } from './sima.ts';
import { simaHall, simaPropsTick } from './sima-bosses.ts';

/** Everything that comes out down here hits as hard as the party is strong. */
const spawn = (s: BattleState, kind: EnemyKind, tier: 1 | 2 | 3, elite: boolean, x: number, y: number, hp = 0): Enemy => {
  const e = spawnEnemy(s, kind, tier, elite, 0, x, y, hp);
  if (s.dungeon) e.damage = mulBp(e.damage, s.dungeon.dmgBp);
  // The deep crypt: every floor is harder than the one above.
  const deep = s.dungeon?.deep;
  if (deep) {
    e.damage = mulBp(e.damage, DEEP_CRYPT.dmgBp[deep.floor] as number);
    if (hp === 0) e.hp = e.maxHp = mulBp(e.maxHp, DEEP_CRYPT.enemyHpBp[deep.floor] as number);
  }
  // The crypt in embers: tougher nightmares (the Warden's health comes already set).
  if (s.rules?.tier === 2 && hp === 0) e.hp = e.maxHp = mulBp(e.maxHp, CRYPT_ASCUAS.enemyHpBp);
  // The day's omen reaches the crypt too (not its Warden): a swarm is frailer, a frenzy faster.
  if (hp === 0 && s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  if (hp === 0 && s.rules?.enemySpeedBp) e.speed = mulBp(e.speed, s.rules.enemySpeedBp);
  ev(s, { t: 'spawn', id: e.id });
  return e;
};
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';

/** How far past its outer cells' centers a room's floor reaches (a cell is ~1000 across; the next cell starts at 500). */
const EDGE = 450;

/** Inside a room: within its radius minus a margin, in world units (cell centers are 1000 apart). */
const inRoom = (room: DungeonRoom, x: number, y: number, margin = 0): boolean => {
  const c = hexToWorld(room.q, room.r);
  const r = room.radius * 1000 + EDGE - margin;
  return dist2(x, y, c.x, c.y) <= r * r;
};

export const standing = (h: Hero): boolean => h.downTicks === 0;

/** Moves (x, y) back inside the room disc if something pushed it out (`edge`: past the outer cells' centers). */
const clampToRoom = (room: DungeonRoom, o: { x: number; y: number }, edge = 0): void => {
  const c = hexToWorld(room.q, room.r);
  const r = room.radius * 1000 + edge;
  const d2 = dist2(o.x, o.y, c.x, c.y);
  if (d2 <= r * r) return;
  const d = isqrt(d2);
  o.x = c.x + idiv((o.x - c.x) * r, d);
  o.y = c.y + idiv((o.y - c.y) * r, d);
};

/**
 * Where a hero can walk while a room's doors are shut: its whole floor, out to the edge drawn on screen. A walk
 * ordered past it ends at it (heroes.ts, moveTarget), so the screen's guess and the room agree and nobody is
 * pulled back from where the screen showed them.
 */
export const shutRoomTarget = (s: BattleState, x: number, y: number): { x: number; y: number } | null => {
  const d = s.dungeon;
  const room = d && !d.exp && !d.maze && d.room >= 0 ? s.map.dungeon?.rooms[d.room] : undefined;
  if (!room) return null;
  const o = { x, y };
  clampToRoom(room, o, EDGE);
  return o;
};

/** Before the party goes down, the crypt's lobby is its entrance: nobody walks on ahead. */
export const holdInEntrance = (s: BattleState): void => {
  const entrance = s.map.dungeon?.rooms[0];
  if (!s.dungeon || !entrance) return;
  if (s.dungeon.maze) {
    holdAtStart(s, s.dungeon.maze);
    return;
  }
  for (const h of s.heroes) clampToRoom(entrance, h);
};

/**
 * One tick of the crypt, before heroes and nightmares act: a room wakes up when a hero walks in (its
 * doors shut and the party is pulled together), waves follow each other, a cleared room opens the way.
 */
export const dungeonTick = (s: BattleState): void => {
  const d = s.dungeon;
  const L = s.map.dungeon;
  if (!d || !L) return;
  if (d.exp) {
    expeditionTick(s, d.exp);
    revive(s);
    return;
  }
  if (d.maze) {
    mazeTick(s, d.maze);
    revive(s);
    return;
  }
  if (d.room < 0) {
    const next = d.cleared.findIndex((c, i) => !c && i > 0);
    const room = next > 0 ? L.rooms[next] : undefined;
    if (room && s.heroes.some((h) => standing(h) && inRoom(room, h.x, h.y, 700))) awaken(s, d, next);
  } else {
    const room = L.rooms[d.room] as DungeonRoom;
    const alive = s.enemies.some((e) => e.hp > 0 && e.kind !== 'boneWall');
    if (d.nextAt > 0 && s.tick >= d.nextAt) {
      d.nextAt = 0;
      d.wave++;
      spawnWave(s, d, d.room, d.wave);
    } else if (!alive && d.nextAt === 0 && (d.room !== L.rooms.length - 1 || d.sima)) {
      // A guardian's hall (deep crypt) is done when its guardian falls; a puzzle room, once solved too.
      if (room.kind === 'boss') clearRoom(s, d);
      else if (d.wave + 1 < DUNGEON.wavesPerRoom) d.nextAt = s.tick + DUNGEON.waveGap;
      else if (!puzzleHolds(d, d.room)) clearRoom(s, d);
    }
    joinTick(s, d, d.room);
    // While the doors are shut nobody leaves the room: not the nightmares, not the heroes (but those walking in).
    for (const e of s.enemies) if (e.hp > 0) clampToRoom(room, e);
    for (const h of s.heroes) if (!d.joining?.includes(h.id)) clampToRoom(room, h, EDGE);
  }
  puzzleTick(s, d, (kind, tier, x, y) => {
    spawn(s, kind, tier, false, x, y);
  });
  descentTick(s, d);
  if (d.sima) {
    simaTick(s, d);
    simaPropsTick(s);
  }
  drinkTick(s, () => {
    if (d.room >= 0) clearRoom(s, d);
  });
  revive(s);
  resolveMarks(s, d);
  burnPools(s, d);
  sealTick(s, d);
};

/**
 * The rune seal on the Warden's door (crypt in embers): once the room it sits in is cleared, the three
 * plates light up in order; stepping on them in that order breaks it. A wrong plate: fire on all three,
 * back to the start, and the order shows again. The next rune counts whenever someone stands on it once
 * the order is over (also whoever was already there while it showed); a wrong one only when stepped onto,
 * and lit ones never, so waiting on a rune is safe.
 */
const sealTick = (s: BattleState, d: DungeonState): void => {
  const seal = d.seal;
  const L = s.map.dungeon;
  if (!seal || seal.solved || !L) return;
  const room = L.rooms.length - 2;
  if (!d.cleared[room]) return;
  const C = CRYPT_ASCUAS.seal;
  const show = (from: number): void => {
    seal.showFrom = from;
    seal.showUntil = from + C.showTicks * seal.plates.length;
    seal.step = 0;
  };
  if (seal.showUntil === 0) show(s.tick + 12);
  const on: number[] = [];
  seal.plates.forEach((p, i) => {
    const c = hexToWorld(p.q, p.r);
    if (s.heroes.some((h) => standing(h) && withinRange(h.x, h.y, c.x, c.y, C.plateRange))) on.push(i);
  });
  const fresh = on.filter((i) => !seal.on.includes(i));
  seal.on = on;
  if (s.tick < seal.showUntil) return;
  const next = seal.order[seal.step] as number;
  const lit = seal.order.slice(0, seal.step);
  const wrong = fresh.find((i) => i !== next && !lit.includes(i));
  if (wrong !== undefined) {
    // Wrong: the runes flare and burst, and show their order again.
    for (const p of seal.plates) {
      const w = hexToWorld(p.q, p.r);
      placeEruption(s, w.x, w.y, CRYPT_ASCUAS.ventWarn);
    }
    show(s.tick + C.replayDelay);
    return;
  }
  if (!on.includes(next)) return;
  const p = seal.plates[next] as { q: number; r: number };
  const c = hexToWorld(p.q, p.r);
  seal.step++;
  ev(s, { t: 'boom', x: c.x, y: c.y, r: C.plateRange, kind: 'rune' });
  if (seal.step < seal.plates.length) return;
  seal.solved = true;
  touchGrid(s);
  const door = L.doors[L.doors.length - 1];
  const dw = door ? hexToWorld(door.q, door.r) : c;
  ev(s, { t: 'skill', hero: 0, skill: 'sealBroken', x: dw.x, y: dw.y });
};

const awaken = (s: BattleState, d: DungeonState, i: number): void => {
  const L = s.map.dungeon;
  if (!L) return;
  const room = L.rooms[i] as DungeonRoom;
  d.room = i;
  d.wave = 0;
  touchGrid(s);
  const c = hexToWorld(room.q, room.r);
  // Nobody fights alone: whoever lingered in the corridor walks in before the door shuts behind them.
  // The glass bridge: everyone onto its first tile at once (the rest is glass).
  const pull = pullSpot(d, i);
  const joining: number[] = [];
  let k = 0;
  for (const h of s.heroes) {
    if (!pull && inRoom(room, h.x, h.y, 300)) continue;
    const ps = pull ? hexToWorld(pull.q, pull.r) : null;
    if (ps) {
      h.x = h.destX = h.ax = ps.x + ((k % 3) - 1) * 150;
      h.y = h.destY = h.ay = ps.y + (k > 2 ? 150 : 0);
      h.moving = false;
      h.path = [];
      k++;
      continue;
    }
    if (standing(h)) joining.push(h.id);
    else pullIn(s, i, h, k);
    k++;
  }
  if (joining.length) {
    d.joining = joining;
    d.joinUntil = s.tick + DUNGEON.joinTicks;
    touchGrid(s);
    k = 0;
    for (const h of s.heroes) if (joining.includes(h.id)) walkIn(s, i, h, k++);
  }
  if (room.kind === 'boss' && d.hallWins) {
    // A saga descent: the last hall is the way out.
    d.reached = true;
    return;
  }
  if (room.kind === 'boss' && d.sima) {
    // The Chasm: a captain, or every fifth floor the stratum's guardian.
    simaHall(s, d, room, (kind, tier, elite, x, y, hp = 0) => spawn(s, kind, tier, elite, x, y, hp));
    return;
  }
  if (room.kind === 'boss' && d.deep) {
    // The deep crypt: each floor's own guardian.
    const kind = guardianOf(room.floor ?? 0);
    const fl = room.floor ?? 0;
    const e = spawn(s, kind, 1, false, c.x, c.y, mulBp(d.deep.hp[fl] ?? d.bossHp, DEEP_CRYPT.guardianHpBp[fl] ?? 10000));
    e.damage = mulBp(e.damage, DEEP_CRYPT.guardianDmgBp[fl] ?? 10000);
    if (kind === 'warden') {
      e.damage = mulBp(e.damage, party(s, WARDEN.meleeByParty));
      e.phase = 1;
      e.timer = WARDEN.every[0] as number;
    }
    d.boss = e.id;
    d.deep.guardian = e.id;
    ev(s, { t: 'skill', hero: e.id, skill: kind === 'warden' ? 'wardenRoar' : 'guardianRise', x: c.x, y: c.y });
    return;
  }
  if (room.kind === 'boss' && s.rules?.drinker) {
    // El pozo 2: the Drinker at the vein, drinking.
    const e = spawn(s, 'drinker', 1, false, c.x, c.y, d.bossHp);
    e.phase = 1;
    e.timer = s.rules.drinker.drink;
    d.boss = e.id;
    ev(s, { t: 'drinker', kind: 'drink', x: c.x, y: c.y });
    return;
  }
  if (room.kind === 'boss') {
    const e = spawn(s, 'warden', 1, false, c.x, c.y, d.bossHp);
    e.damage = mulBp(e.damage, party(s, WARDEN.meleeByParty));
    e.phase = 1;
    e.timer = WARDEN.every[0] as number;
    d.boss = e.id;
    ev(s, { t: 'skill', hero: e.id, skill: 'wardenRoar', x: c.x, y: c.y });
  } else {
    spawnWave(s, d, i, 0);
    wakePuzzle(s, d, i);
  }
};

/** The spot just inside room `i` by its door where the `k`-th hero from the corridor ends up. */
const entrySpot = (s: BattleState, i: number, k: number): { x: number; y: number } => {
  const L = s.map.dungeon;
  const room = L?.rooms[i] as DungeonRoom;
  const door = L?.doors[i - 1];
  const c = hexToWorld(room.q, room.r);
  const dw = door ? hexToWorld(door.q, door.r) : c;
  const dd = isqrt(dist2(dw.x, dw.y, c.x, c.y)) || 1;
  const reach = (room.radius - 1) * 1000;
  return { x: c.x + idiv((dw.x - c.x) * reach, dd) + ((k % 3) - 1) * 500, y: c.y + idiv((dw.y - c.y) * reach, dd) + (k > 5 ? -500 : k > 2 ? 500 : 0) };
};

const walkIn = (s: BattleState, i: number, h: Hero, k: number): void => {
  const p = entrySpot(s, i, k);
  orderMove(s, h, p.x, p.y);
};

/** Into room `i` at once: a fallen hero, or one who didn't walk in while the door waited. */
const pullIn = (s: BattleState, i: number, h: Hero, k: number): void => {
  const p = entrySpot(s, i, k);
  h.x = h.destX = h.ax = p.x;
  h.y = h.destY = h.ay = p.y;
  h.moving = false;
  h.path = [];
};

/**
 * The heroes walking in from the corridor: each one inside (or fallen) stops counting; one who stopped short is
 * sent on again; when the wait is over the rest are brought in, and once nobody is left the door shuts.
 */
const joinTick = (s: BattleState, d: DungeonState, i: number): void => {
  if (!d.joining?.length) return;
  const room = s.map.dungeon?.rooms[i] as DungeonRoom;
  const over = s.tick >= (d.joinUntil ?? 0);
  let k = 0;
  d.joining = d.joining.filter((id) => {
    const h = findHero(s, id);
    if (!h || inRoom(room, h.x, h.y, 300)) return false;
    if (over || !standing(h)) {
      pullIn(s, i, h, k++);
      return false;
    }
    if (!h.moving) walkIn(s, i, h, k);
    k++;
    return true;
  });
  if (d.joining.length) return;
  delete d.joining;
  delete d.joinUntil;
  touchGrid(s);
};

const spawnWave = (s: BattleState, d: DungeonState, room: number, wave: number): void => {
  for (const sp of d.spawns) {
    if (sp.room !== room || sp.wave !== wave) continue;
    const p = hexToWorld(sp.q, sp.r);
    const e = spawn(s, sp.kind, sp.tier, sp.elite, p.x, p.y);
    // Two elites where one was (the Chasm's pair sign): each a share of the one.
    if (sp.hpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, sp.hpBp));
  }
};

const clearRoom = (s: BattleState, d: DungeonState): void => {
  const hall = s.map.dungeon?.rooms[d.room]?.kind === 'boss';
  d.cleared[d.room] = true;
  d.room = -1;
  d.wave = 0;
  d.marks = [];
  d.pools = [];
  touchGrid(s);
  for (const h of s.heroes) {
    // Anchored where they stand: idle heroes drift back to their anchor in a straight line, never through a shut door.
    h.ax = h.x;
    h.ay = h.y;
    if (h.downTicks > 0) getUp(s, h, DUNGEON.clearReviveHpBp);
    else {
      h.hp += mulBp(h.stats.maxHp, s.rules?.tier === 2 ? CRYPT_ASCUAS.clearHealBp : s.rules?.tier === 3 ? DEEP_CRYPT.clearHealBp : DUNGEON.clearHealBp);
      if (h.hp > h.stats.maxHp) h.hp = h.stats.maxHp;
    }
  }
  if (hall && d.sima) simaHallCleared(s, d);
  else if (hall) openDescent(s, d);
};

/** A fallen hero gets up where it lies, with this share of its life. */
export const getUp = (s: BattleState, h: Hero, hpBp: number): void => {
  // Eaten by the dragon of the Burn: out of the fight for good, nothing raises it (docs/UNICAS.md §4.4).
  if (s.dragon?.eaten.includes(h.pid)) return;
  h.downTicks = 0;
  h.revive = 0;
  h.hp = mulBp(h.stats.maxHp, hpBp);
  if (h.hp < 1) h.hp = 1;
  h.anim = 'idle';
  h.animT = 0;
  h.invuln = 12;
  ev(s, { t: 'up', id: h.id });
};

/** A standing ally over a fallen hero brings them back. */
const revive = (s: BattleState): void => {
  for (const h of s.heroes) {
    if (h.downTicks === 0) continue;
    const helper = s.heroes.some((o) => o !== h && standing(o) && withinRange(o.x, o.y, h.x, h.y, DUNGEON.reviveRange));
    h.revive = helper ? (h.revive ?? 0) + 1 : 0;
    if ((h.revive ?? 0) >= DUNGEON.reviveTicks) getUp(s, h, DUNGEON.reviveHpBp);
  }
};

export const insideMark = (m: DungeonMark, x: number, y: number): boolean => {
  const d2 = dist2(x, y, m.x, m.y);
  return m.kind === 'ring' ? d2 > m.r * m.r : d2 <= m.r * m.r;
};

const resolveMarks = (s: BattleState, d: DungeonState): void => {
  if (!d.marks.length) return;
  const keep: DungeonMark[] = [];
  for (const m of d.marks) {
    if (m.at > s.tick) {
      keep.push(m);
      continue;
    }
    // The Warden's marks are its marked blow: grave dust (cinders in the crypt in embers) on whoever they catch twice.
    for (const h of s.heroes) {
      if (!standing(h) || !insideMark(m, h.x, h.y)) continue;
      damageHero(s, h, m.dmg);
      markedBlow(s, h, s.rules?.tier === 2 ? 'cinders' : 'graveDust');
    }
    ev(s, { t: 'boom', x: m.x, y: m.y, r: m.r, kind: m.kind });
    // In the last phase a slam leaves burning ink behind.
    const boss = s.enemies.find((e) => e.id === d.boss);
    if (m.kind === 'slam' && boss && boss.phase >= 3) d.pools.push({ id: s.nextId++, x: m.x, y: m.y, r: WARDEN.poolRadius, until: s.tick + WARDEN.poolTicks });
  }
  d.marks = keep;
};

const burnPools = (s: BattleState, d: DungeonState): void => {
  if (!d.pools.length) return;
  d.pools = d.pools.filter((p) => p.until > s.tick);
  if (s.tick % WARDEN.poolEvery !== 0) return;
  for (const p of d.pools) for (const h of s.heroes) if (standing(h) && withinRange(h.x, h.y, p.x, p.y, p.r)) damageHero(s, h, WARDEN.poolDamage);
};

/** Is this spot about to be hit (a mark landing within `ticks`) or burning? */
export const dangerAt = (s: BattleState, x: number, y: number, ticks = 0x7fffffff): boolean => {
  const d = s.dungeon;
  if (!d) return false;
  for (const m of d.marks) if (m.at - s.tick <= ticks && insideMark(m, x, y)) return true;
  for (const p of d.pools) if (withinRange(x, y, p.x, p.y, p.r)) return true;
  return false;
};

/** The one a fight is about: a crypt's boss, the battle's boss, a Prestacaras or one of the Unsleeping. */
const huntBoss = (s: BattleState, e: Enemy): boolean => e.id === s.bossId || e.id === s.dungeon?.boss || e.mask !== undefined || e.un !== undefined;

/**
 * Crypt nightmares hunt heroes: whoever taunted them, else an automaton that draws them (a boss only for a moment,
 * docs/DECISIONS.md 2026-11-02; only when the caller gives `hit`), else the nearest hero standing.
 */
export const chaseHero = (s: BattleState, e: Enemy, movePx: (e: Enemy) => number, attack: (h: Hero) => void, hit?: (b: Bot) => void): void => {
  let h: Hero | null = e.taunter > 0 ? findHero(s, e.taunter) : null;
  if (h && !standing(h)) h = null;
  const b = !h && hit ? huntDrawnBy(s, e, huntBoss(s, e)) : null;
  if (b && hit) {
    e.tgtKind = 0;
    e.tgtId = 0;
    if (withinRange(e.x, e.y, b.x, b.y, NIGHTMARES[e.kind].reach + 400)) hit(b);
    else {
      e.anim = 'walk';
      stepToward(e, b.x, b.y, movePx(e));
    }
    return;
  }
  if (!h) {
    let bd = 0x7fffffff;
    h = null;
    for (const o of s.heroes) {
      if (!standing(o)) continue;
      const dd = dist2(e.x, e.y, o.x, o.y);
      if (dd < bd) {
        bd = dd;
        h = o;
      }
    }
  }
  if (!h) {
    e.anim = 'walk';
    return;
  }
  e.tgtKind = 1;
  e.tgtId = h.id;
  if (withinRange(e.x, e.y, h.x, h.y, NIGHTMARES[e.kind].reach + 150)) attack(h);
  else {
    e.anim = 'walk';
    stepToward(e, h.x, h.y, movePx(e));
  }
};

/** Living heroes, in id order (for targeted abilities). */
const standingHeroes = (s: BattleState): Hero[] => s.heroes.filter(standing);

/**
 * The Marrow Warden: cleaves whoever is in front of it and, every few seconds, reads the floor —
 * slams under a hero, a nova around itself (get away) or a ring beyond (come close). It roars at 60 %
 * and 25 %, calling bones from the walls; in the last phase it is faster and its slams leave ink.
 */
export const wardenTick = (s: BattleState, e: Enemy, movePx: (e: Enemy) => number, attack: (h: Hero) => void, hit?: (b: Bot) => void): void => {
  const d = s.dungeon;
  if (!d) return;
  const hpBp = idiv(e.hp * 10000, e.maxHp || 1);
  if (e.phase === 1 && hpBp <= WARDEN.phase2AtBp) phaseUp(s, d, e, 2);
  else if (e.phase === 2 && hpBp <= WARDEN.phase3AtBp) phaseUp(s, d, e, 3);
  if (d.roarUntil > s.tick) {
    e.anim = 'attack';
    return;
  }
  e.timer--;
  if (e.timer <= 0) {
    cast(s, d, e);
    e.timer = WARDEN.every[e.phase - 1] as number;
  }
  chaseHero(s, e, movePx, (h) => {
    attack(h);
    // The cleave catches whoever stands next to its target.
    if (e.cd === e.reload) for (const o of s.heroes) if (o !== h && standing(o) && withinRange(o.x, o.y, h.x, h.y, WARDEN.cleaveRadius)) damageHero(s, o, idiv(e.damage, 2));
  }, hit);
};

const phaseUp = (s: BattleState, d: DungeonState, e: Enemy, phase: number): void => {
  e.phase = phase;
  d.roarUntil = s.tick + WARDEN.roarTicks;
  d.marks = [];
  if (phase === 3) e.speed = mulBp(e.speed, WARDEN.phase3SpeedBp);
  ev(s, { t: 'skill', hero: e.id, skill: 'wardenRoar', x: e.x, y: e.y });
  summon(s, e, party(s, WARDEN.roarAdds), phase === 3 ? 'wraith' : 'skeleton');
};

/** A per-party-size number (index = heroes in the crypt). */
const party = (s: BattleState, table: readonly number[]): number => table[s.heroes.length < table.length ? s.heroes.length : table.length - 1] as number;

const summon = (s: BattleState, e: Enemy, n: number, kind: 'skeleton' | 'wraith'): void => {
  const L = s.map.dungeon;
  // In the Warden's hall (the deep crypt's first floor has its own).
  const room = L?.rooms[s.dungeon && s.dungeon.room >= 0 ? s.dungeon.room : L.rooms.length - 1];
  if (!room) return;
  const edge = hexRing(room.radius - 1);
  for (let i = 0; i < n; i++) {
    const h = edge[rngInt(s.rng, edge.length)] as { q: number; r: number };
    const p = hexToWorld(room.q + h.q, room.r + h.r);
    spawn(s, kind, 1, false, p.x, p.y);
  }
};

const mark = (s: BattleState, d: DungeonState, kind: DungeonMark['kind'], x: number, y: number, r: number, warn: number, dmg: number): void => {
  // The Chasm's floors set their own blows (the Warden guards its ossuary).
  const bp = d.sima ? mulBp(d.sima.dmgBp, simaDmgBp(d.sima.floor)) : d.dmgBp;
  d.marks.push({ id: s.nextId++, kind, x, y, r, from: s.tick, at: s.tick + warn, dmg: mulBp(dmg, bp) });
};

const cast = (s: BattleState, d: DungeonState, e: Enemy): void => {
  const heroes = standingHeroes(s);
  if (!heroes.length) return;
  const n = d.casts++;
  const slamOn = (h: Hero): void => mark(s, d, 'slam', h.x, h.y, WARDEN.slamRadius, WARDEN.slamWarn, WARDEN.slamDamage);
  const area = (): void => {
    // Alternate: a nova (run) and a ring (close in).
    const ring = (idiv(n, 3) & 1) === 1;
    mark(s, d, ring ? 'ring' : 'nova', e.x, e.y, WARDEN.novaRadius, WARDEN.novaWarn, WARDEN.novaDamage);
  };
  const pickHero = (): Hero => heroes[rngInt(s.rng, heroes.length)] as Hero;
  if (e.phase === 1) {
    slamOn(pickHero());
    if (n % WARDEN.summonEvery === WARDEN.summonEvery - 1) summon(s, e, party(s, WARDEN.summonCount), 'skeleton');
  } else if (e.phase === 2) {
    if (n % 3 === 1) area();
    else slamOn(pickHero());
    if (n % 3 === 2) summon(s, e, party(s, WARDEN.summonCount), 'wraith');
  } else {
    // Two slams at once, on two different heroes when there are two.
    const a = pickHero();
    slamOn(a);
    const others = heroes.filter((h) => h !== a);
    if (others.length) slamOn(others[rngInt(s.rng, others.length)] as Hero);
    if (n % 2 === 1) area();
  }
  ev(s, { t: 'skill', hero: e.id, skill: 'wardenCast', x: e.x, y: e.y });
};

/** New crypt state for a battle: the entrance counts as cleared. */
export const newDungeonState = (rooms: number, spawns: DungeonState['spawns'], bossHp: number, dmgBp: number): DungeonState => ({
  room: -1,
  wave: 0,
  cleared: Array.from({ length: rooms }, (_, i) => i === 0),
  spawns,
  marks: [],
  pools: [],
  boss: 0,
  bossHp,
  roarUntil: 0,
  casts: 0,
  dmgBp,
  nextAt: 0,
});
